/**
 * Loading, empty and error states.
 *
 * These three are where "designed" and "generated" diverge most visibly,
 * because generated UIs ship the happy path and a spinner. Skeletons match the
 * real content's shape, and "nothing exists yet" is a different state from
 * "nothing matches this filter" -- conflating them confuses staff daily.
 */
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  CheckCircle2,
  Inbox,
  Lock,
  SearchX,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

export function TableSkeleton({
  rows = 8,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex h-row-default items-center gap-4 border-b border-line-faint px-4"
        >
          {Array.from({ length: columns }).map((_, c) => (
            <div
              key={c}
              className="h-3 animate-pulse rounded-xs bg-surface-sunken"
              // Varied widths so the skeleton reads as content, not a barcode.
              style={{ width: `${[16, 28, 22, 12, 18, 14][c % 6]}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function StateShell({
  icon: Icon,
  title,
  body,
  action,
  className,
}: {
  icon: LucideIcon;
  title: string;
  body?: string | undefined;
  action?: ReactNode | undefined;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center px-6 py-16 text-center",
        className,
      )}
      role="status"
    >
      <Icon
        className="size-8 text-ink-subtle"
        aria-hidden="true"
        strokeWidth={1.75}
      />
      <p className="mt-4 text-lg font-semibold text-ink">{title}</p>
      {body ? (
        <p className="mt-1 max-w-sm text-md text-ink-muted">{body}</p>
      ) : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

/**
 * Nothing recorded yet. The one empty state for every list and panel -- a
 * table, a fund ledger or a recent-receipts list all say "nothing here" the
 * same way. The second line is optional for a panel whose title says it all.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string | undefined;
  action?: ReactNode | undefined;
}) {
  // A heading, so no full stop -- some of the strings behind it are written
  // as sentences, and side by side "No staff yet" and "No jobs recorded." read
  // as two different designs.
  return (
    <StateShell
      icon={Inbox}
      title={title.replace(/\.$/, "")}
      body={body}
      action={action}
    />
  );
}

/**
 * This screen is not for this role.
 *
 * Distinct from `ErrorState`: nothing went wrong and retrying will not help.
 * Reached by typing or bookmarking a path the sidebar does not offer -- those
 * used to render the page shell around a permission error, or in the case of
 * the dashboards, a loading skeleton that never resolved.
 */
export function NoAccessState({ action }: { action?: ReactNode | undefined }) {
  const { t } = useTranslation();
  return (
    <StateShell
      icon={Lock}
      title={t("state.noAccessTitle")}
      body={t("state.noAccessBody")}
      action={action}
    />
  );
}

/** Records exist, but none match the current filters. Offers a way back. */
export function NoResultsState({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <StateShell
      icon={SearchX}
      title={t("state.noResultsTitle")}
      body={t("state.noResultsBody")}
      action={
        <Button variant="secondary" onClick={onClear}>
          {t("action.clearFilters")}
        </Button>
      }
    />
  );
}

/**
 * Errors say what failed and what to do. Never a raw exception, never
 * "Something went wrong" with no next step.
 */
export function ErrorState({
  code,
  onRetry,
}: {
  code?: string | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const message = t([
    `errors.${code ?? "UNKNOWN_ERROR"}`,
    "errors.UNKNOWN_ERROR",
  ]);
  return (
    <StateShell
      icon={AlertTriangle}
      title={t("state.errorTitle")}
      body={message}
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            {t("action.retry")}
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * "That went in": the banner a data-entry screen shows after a save, with
 * what to do next.
 *
 * For the screens where the thing saved is not visible on the page afterwards
 * -- the form clears for the next family -- so a toast would be gone before
 * the clerk has read the receipt number off it to the person waiting.
 */
export function SavedNotice({
  title,
  children,
  actions,
}: {
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      role="status"
      className="rounded-lg border border-card-edge bg-success-soft px-5 py-4 text-success-soft-ink"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold">{title}</p>
          {children ? (
            <div className="mt-1 space-y-1 text-sm">{children}</div>
          ) : null}
          {actions ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
